/**
 * @voltxampere/web — OpAmp model presets (Sprint 23 D3)
 *
 * Sprint 22 D-22-4 closure'da `gainBandwidthProduct` (GBW) schema
 * field'ı eklendi. UI dropdown S23+'a deferred edildi — Sprint 23
 * D3 inspector'a 4 hazır preset (Custom + µA741 + LM358 + TL072)
 * dropdown'u ekler.
 *
 * Preset seçimi component params'ı multi-update eder
 * (`openLoopGain` + `gainBandwidthProduct` aynı anda). "Custom"
 * preset hiçbir şey yazmaz — kullanıcı manual girer; matchPreset
 * mevcut params'a uyan preset bulamadığında "custom" döner.
 *
 * Schema değiştirilmez — preset seçimi UI-only state, params
 * değişince Custom otomatik gösterilir.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

export interface OpAmpPreset {
  readonly id: string;
  readonly labelKey: string; // i18n key
  /** Open-loop gain `A0`. Custom preset: undefined. */
  readonly openLoopGain?: number;
  /** Gain-bandwidth product (Hz). Custom preset: undefined. */
  readonly gainBandwidthProduct?: number;
}

/**
 * Şefim'in önerdiği 3 industry-standard op-amp + Custom (kullanıcı
 * manual). ADR-0047 §"OpAmp model_variant dropdown (D3)".
 */
export const OPAMP_PRESETS: readonly OpAmpPreset[] = [
  { id: "custom", labelKey: "inspector.opamp.preset.custom" },
  // µA741 — industry classic op-amp, A0 ≈ 200000, GBW ≈ 1 MHz
  {
    id: "ua741",
    labelKey: "inspector.opamp.preset.ua741",
    openLoopGain: 2e5,
    gainBandwidthProduct: 1e6,
  },
  // LM358 — single-supply popular op-amp, A0 ≈ 100000, GBW ≈ 1 MHz
  {
    id: "lm358",
    labelKey: "inspector.opamp.preset.lm358",
    openLoopGain: 1e5,
    gainBandwidthProduct: 1e6,
  },
  // TL072 — JFET input low-noise, A0 ≈ 200000, GBW ≈ 3 MHz
  {
    id: "tl072",
    labelKey: "inspector.opamp.preset.tl072",
    openLoopGain: 2e5,
    gainBandwidthProduct: 3e6,
  },
];

const TOLERANCE = 1e-6;

/**
 * Mevcut OpAmp params'a uygun preset id'sini bul. Tam eşleşme yoksa
 * "custom" döner. Kullanıcı bir preset seçtikten sonra param'ı
 * manual değiştirirse otomatik "custom"a düşer (UI dropdown
 * "Custom"a kayar).
 */
export function matchOpAmpPreset(params: Readonly<Record<string, unknown>>): string {
  const gain = typeof params.openLoopGain === "number" ? params.openLoopGain : undefined;
  const gbw =
    typeof params.gainBandwidthProduct === "number" ? params.gainBandwidthProduct : undefined;
  for (const preset of OPAMP_PRESETS) {
    if (preset.id === "custom") continue;
    if (
      preset.openLoopGain !== undefined &&
      preset.gainBandwidthProduct !== undefined &&
      gain !== undefined &&
      gbw !== undefined &&
      Math.abs(gain - preset.openLoopGain) / preset.openLoopGain < TOLERANCE &&
      Math.abs(gbw - preset.gainBandwidthProduct) / preset.gainBandwidthProduct < TOLERANCE
    ) {
      return preset.id;
    }
  }
  return "custom";
}
